import { TCommonVipBadgeTypeV2 } from '~/api/converters/common/V2/VipDiscountBadge/index.type'
import {
  MemberClassBadge,
  MemberClassBenefitsResponse,
  TMemberClassBadge,
  TMemberClassBenefit,
  TMemberClassGlobalBadge,
} from '~/api/services/member-class-api/index.type'
import {
  CommonVipBadgeType,
  TCommonVipBadgeType,
} from '~/api/converters/global/common/VipDiscountBadge/index.type'
import {
  AriProxyApiRatePlanPriceType,
  TAriProxyApiRatePlanPriceType,
} from '~/api/services/ari-proxy-api/index.type'
import { toKRW, toPoint } from '~/api/utils/krNumberToString'
import { MemberClassRewardProps } from '~/app/components-nds/common/member-class-reward'
import {
  GlobalOrderPaymentType,
  IGlobalOrderApiCancellationPreview,
  IGlobalOrderApiDetail,
} from '~/api/services/global-order-api/index.type'

/**
 * @deprecated 구버전 LEVEL_XX 등급 코드 체계. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 */
export const MemberClassMap = {
  LEVEL_00: 0, // 일반
  LEVEL_01: 1, // 골드
  LEVEL_02: 2, // 핑크
  LEVEL_03: 3, // 로얄 핑크
}
/**
 * @deprecated 구버전 LEVEL_XX 등급 코드 체계. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 */
export type MemberClass = keyof typeof MemberClassMap

/**
 * @summary 회원 등급 뱃지 서열 (신규 회원 등급 체계)
 */
export const MemberClassBadgeRank: Record<TMemberClassBadge, number> = {
  SILVER_CLASS: 0,
  GOLD_CLASS: 1,
  PLATINUM_CLASS: 2,
}

/**
 * @summary 해외주문 VIP 요금 여부 확인 > 가격 타입 확인
 * @description B2B 가격 타입인 경우 골드 클래스 타입.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param priceType
 */
export const getGlobalVipBadgeType = (priceType: TAriProxyApiRatePlanPriceType) => {
  switch (priceType) {
    case AriProxyApiRatePlanPriceType.B2B:
      return CommonVipBadgeType.GOLD
    case AriProxyApiRatePlanPriceType.FLIGHT:
      return CommonVipBadgeType.FLIGHT
    default:
      return undefined
  }
}

/**
 * @summary 해외주문 VIP 요금 여부 확인 > 할인 적용 금액 노출 조건
 * @description 할인 적용 금액이 1000원 이상인 경우에만 안내 영역 노출
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param discountAmount 할인되는 금액
 */
export const getIsGlobalVipDiscountAmount = (discountAmount: number) => {
  return discountAmount >= 1000
}

/**
 * @summary VIP 요금 여부 확인 > 할인 금액 안내
 * @description VIP 할인 금액이 1000원 이상인 경우에만 금액 반환
 * @param discountAmount 할인되는 금액
 */
export const getVipDiscountNoticeAmountText = ({
  priceType,
  discountAmount,
}: {
  priceType: TAriProxyApiRatePlanPriceType | undefined
  discountAmount: number | undefined
}): string => {
  if (!priceType || !discountAmount) {
    return ''
  }

  // 가격 타입이 Vip 요금제인지 (Gold 등급)
  const isVip = getGlobalVipBadgeType(priceType) === CommonVipBadgeType.GOLD
  // 할인 금액이 1000원 이상인지
  const isVipDiscount = getIsGlobalVipDiscountAmount(discountAmount)

  return !!isVip && isVipDiscount ? toKRW(discountAmount) : ''
}

/**
 * for V2
 */
export const getGlobalVipDiscountAmount = ({
  priceType,
  discountAmount,
}: {
  priceType: TAriProxyApiRatePlanPriceType | undefined
  discountAmount: number | undefined
}): number => {
  if (!priceType || !discountAmount) {
    return 0
  }

  const isVip = getGlobalVipBadgeType(priceType) === CommonVipBadgeType.GOLD
  const isVipDiscount = getIsGlobalVipDiscountAmount(discountAmount)

  return !!isVip && isVipDiscount ? discountAmount : 0
}

/**
 * @deprecated 구버전 LEVEL_XX 등급 코드 기반. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 */
export const getVipMemberClass = (memberClass?: string): MemberClass | undefined => {
  return memberClass && MemberClassMap[memberClass] > 0 ? (memberClass as MemberClass) : undefined
}

/**
 * @deprecated 구버전 LEVEL_XX 등급 코드 기반. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 */
export const getDomesticVipBadgeType = (data?: string): TCommonVipBadgeType | '' => {
  const memberClass = getVipMemberClass(data)
  switch (memberClass) {
    case 'LEVEL_01':
      return CommonVipBadgeType.GOLD
    default:
      return ''
  }
}

/**
 * @deprecated 구버전 LEVEL_XX 등급 코드 기반. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 * @summary 해외주문 VIP 요금 여부 확인 > 가격 타입 확인 V2
 * @description B2B 가격 타입인 경우 골드 클래스 타입.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29233997/B2B+API}
 * @param priceType
 */
export const getGlobalVipBadgeTypeV2 = (priceType: TAriProxyApiRatePlanPriceType) => {
  switch (priceType) {
    case AriProxyApiRatePlanPriceType.B2B:
      return CommonVipBadgeType.GOLD
    default:
      return undefined
  }
}

/**
 * @summary 국내주문 VIP 요금 여부 확인 V2
 * @deprecated 구버전 LEVEL_XX 등급 코드 기반. 신규 코드는 회원 등급 뱃지(`TMemberClassBadge`) 기반을 사용한다.
 *
 * ⚠️ 현재 프로덕션 사용처가 없고 테스트에서만 참조된다.
 * 회원 등급 뱃지 전환이 완료되기 전까지는 삭제하지 말고 유지한다. 이후 별도 정리 작업에서 삭제 검토.
 */
export const getDomesticVipBadgeTypeV2 = (data?: string): TCommonVipBadgeTypeV2 | '' => {
  const memberClass = getVipMemberClass(data)
  switch (memberClass) {
    case 'LEVEL_01':
      return CommonVipBadgeType.GOLD
    default:
      return ''
  }
}

/**
 * @summary 회원 등급 파라미터 생성 (통합주문)
 * @description 회원 등급 혜택 조회 실패(null) 또는 badge 미존재 시 SILVER_CLASS 기본값을 적용한다
 */
export const getMemberClassParams = (
  memberClassBenefits?: MemberClassBenefitsResponse | null
): { memberClassBadge: TMemberClassBadge; memberClassBenefits: TMemberClassBenefit[] } => {
  return {
    memberClassBadge: memberClassBenefits?.badge ?? 'SILVER_CLASS',
    memberClassBenefits: memberClassBenefits?.benefits ?? [],
  }
}

/**
 * @summary 회원 등급 파라미터 생성 (해외주문)
 * @description 해외주문용 뱃지(`globalBadge`) 기준. 회원 등급 혜택 조회 실패(null) 또는 globalBadge 미존재 시 SILVER_CLASS 기본값을 적용한다
 */
export const getGlobalMemberClassParams = (
  memberClassBenefits?: MemberClassBenefitsResponse | null
): { memberClassBadge: TMemberClassGlobalBadge; memberClassBenefits: TMemberClassBenefit[] } => {
  return {
    memberClassBadge: memberClassBenefits?.globalBadge ?? 'SILVER_CLASS',
    memberClassBenefits: memberClassBenefits?.benefits ?? [],
  }
}

/**
 * @summary 해외주문 Post Order > 회원 등급으로 인한 할인 가격 계산
 *  - 예약완료 / 예약상세
 * @description 할인 적용 금액이 1000원 이상인 경우에만 안내 영역 노출
 */
export const getGlobalPostOrderMemberClassDiscountAmount = ({
  memberClassBadge,
  discountAmount,
}: {
  memberClassBadge: TMemberClassGlobalBadge
  discountAmount?: number
}): number => {
  return memberClassBadge !== 'FLIGHT_ORDER' &&
    memberClassBadge !== 'SILVER_CLASS' &&
    discountAmount &&
    discountAmount >= 1000
    ? discountAmount
    : 0
}

/**
 * @summary 해외주문 Post Order(예약 완료/예약상세) > 회원 등급에 따른 구매 적립 NOL 포인트 계산
 * @description 회원 등급 뱃지가 FLIGHT_ORDER인 경우 0을 반환
 * @param isMember 회원 여부
 * @param memberClassBadge 회원 등급 뱃지
 * @param payments 결제 정보
 * @param totalRewardAmount 구매 적립 NOL 포인트
 */
export const getGlobalPostOrderMemberClassRewardData = ({
  isMember,
  memberClassBadge,
  payments,
  totalRewardAmount,
}: {
  isMember: boolean
  memberClassBadge: TMemberClassGlobalBadge
  payments: IGlobalOrderApiDetail['payments'] | IGlobalOrderApiCancellationPreview['payments']
  totalRewardAmount?: number
}): Pick<MemberClassRewardProps, 'rewardText' | 'memberClassBadge' | 'rewards'> | undefined => {
  if (!totalRewardAmount || !isMember || memberClassBadge === 'FLIGHT_ORDER') return undefined

  const rewardText = `최대 ${toPoint(totalRewardAmount)} 적립`
  const pgPayment = payments.find(({ type }) => type === GlobalOrderPaymentType.PG)?.method
  const rewardLabel =
    pgPayment === 'NOL_MONEY' ? 'NOL 머니 결제 시' : '일반 결제 (NOL 머니 제외) 시'

  return {
    rewardText,
    memberClassBadge,
    rewards: [{ label: rewardLabel, value: rewardText }],
  }
}

// MemberClassBadge 키 선언 순서(등급 오름차순)를 우선순위로 사용 — 등급 추가 시 원천만 수정하면 된다.
const BADGE_GRADE_DESC = (Object.keys(MemberClassBadge) as TMemberClassBadge[]).reverse()

/**
 * @summary 등급 뱃지 목록에서 최고 등급 뱃지 선택
 * @description 카테고리 주문별 뱃지가 섞여 있으면 가장 높은 등급을 대표로 사용한다.
 * 유효한 뱃지가 없으면 SILVER_CLASS 기본값을 적용한다.
 */
export const getHighestMemberClassBadge = (
  badges: ReadonlyArray<string | null | undefined>
): TMemberClassBadge => {
  return BADGE_GRADE_DESC.find(badge => badges.includes(badge)) ?? MemberClassBadge.SILVER_CLASS
}
